import { useTheme } from '@mui/material';
import { useCreateEncounter, useUpdateEncounter } from '_api';
import { EncounterPayload } from '_api/types';
import { SEVERITY } from '_constants/derived';
import { SecondaryButton } from 'components/atoms';
import { AlertMessage, BaseModal } from 'components/molecules';
import dayjs from 'dayjs';
import { noop } from 'lodash';
import React, { useState } from 'react';
import { FaEdit } from 'react-icons/fa';

import { EncounterForm } from '../forms';

import { EncounterModalProps } from './types';

export const EncounterModal = ({
  initValue,
  editing,
  onEdit,
  open,
  patientId,
  onClose,
  refetch = noop,
}: EncounterModalProps) => {
  const theme = useTheme();
  const { mutateAsync: createEncounter } = useCreateEncounter();
  const { mutateAsync: updateEncounter } = useUpdateEncounter();
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const onSubmit = (payload: EncounterPayload) => {
    const { interactionOccurred: interactionOccurred, ...payloadData } = payload;
    const submitPayload: EncounterPayload = {
      ...payloadData,
      patient_id: patientId,
      date_of_observation: dayjs(payload.date_of_observation, 'MM/DD/YYYY').format(
        'YYYY-MM-DD',
      ),
      interactionOccurred: interactionOccurred,
      contactMethod: interactionOccurred ? payloadData.contactMethod : undefined,
    };
    if (submitPayload['id'] === undefined) {
      createEncounter(submitPayload)
        .then(() => {
          setSuccessMessage('Encounter successfully created.');
          refetch();
          setShowSuccess(true);
          setTimeout(() => {
            setShowSuccess(false);
            onClose();
          }, 1500);
        })
        .catch((err: Error) => {
          setErrorMessage('Failed to create Encounter...' + err.message);
          setShowError(true);
        });
    } else {
      updateEncounter(submitPayload)
        .then(() => {
          setSuccessMessage('Encounter successfully updated.');
          setShowSuccess(true);
          setTimeout(() => {
            setShowSuccess(false);
            onClose();
          }, 1500);
        })
        .catch((err: Error) => {
          setErrorMessage('Failed to update Encounter... ' + err.message);
          setShowError(true);
        });
    }
  };

  return (
    <BaseModal
      open={open}
      title={`${!editing && initValue.id === undefined ? 'Add ' : ''}Remote Monitoring Encounter`}
      onClose={onClose}
    >
      <EncounterForm
        initValue={initValue}
        onSubmit={onSubmit}
        onBack={onClose}
        disableAll={initValue.id != undefined && !editing}
        hideSubmitButtons={initValue.id != undefined && !editing}
      />
      {initValue.id != undefined && !editing ? (
        <SecondaryButton
          onClick={() => onEdit(true)}
          sx={{
            color: theme.palette.primary.main,
            float: 'right',
          }}
        >
          <span> Edit </span>
          <FaEdit style={{ marginLeft: '5px' }} />
        </SecondaryButton>
      ) : null}
      <AlertMessage
        open={showSuccess}
        onClose={() => setShowSuccess(false)}
        message={successMessage}
      />
      <AlertMessage
        open={showError}
        onClose={() => setShowError(false)}
        severity={SEVERITY.error}
        message={errorMessage}
      />
    </BaseModal>
  );
};
